import {
  Activity,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Box,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { DataGrid, GridPagination } from '@mui/x-data-grid'

import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FileDownload from '@mui/icons-material/FileDownload'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import handleExport from '../utilities/export'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'

const HoloTable = ({
  id,
  data,
  selectedRow,
  columns,
  title,
  onRowClick,
  pageSizeOptions = [5, 10, 25, 100],
  pageSize = 10,
  showActions = false,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  slots = {},
  disableColumnSelector = true,
  rowCount = 0,
  checkboxSelection = false,
  ...params
}) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const navigate = useNavigate()
  const handleMenu = (event) => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const mountedRef = useRef(false)
  useEffect(() => {
    mountedRef.current = true
  }, [])

  const processedColumns = useMemo(() => {
    const base = isMobile
      ? columns.map((col, i) =>
          i === 0
            ? { ...col, flex: 2, minWidth: 150, resizable: true }
            : { ...col, flex: 1, minWidth: 100, resizable: true },
        )
      : columns
    return base.map((col) => ({
      ...col,
      renderCell:
        col.renderCell ??
        ((params) => params.formattedValue ?? params.value ?? ''),
      renderHeader:
        col.renderHeader ?? ((params) => params.colDef.headerName ?? ''),
    }))
  }, [columns, isMobile])

  const handleRowClick = (params) => {
    navigate(`/${id}/${params.row.uuid || params.row.id}`)
  }

  const handleSortModelChange = useCallback(
    (model) =>
      queueMicrotask(() => {
        if (mountedRef.current)
          startTransition(() => onSortModelChange?.(model))
      }),
    [onSortModelChange],
  )
  const handleFilterModelChange = useCallback(
    (model) =>
      queueMicrotask(() => {
        if (mountedRef.current)
          startTransition(() => onFilterModelChange?.(model))
      }),
    [onFilterModelChange],
  )
  const handlePaginationModelChange = useCallback(
    (model) =>
      queueMicrotask(() => {
        if (mountedRef.current)
          startTransition(() => onPaginationModelChange?.(model))
      }),
    [onPaginationModelChange],
  )

  return (
    <Grid container size={12}>
      <Grid size={12} sx={{ m: title ? 2 : 0 }}>
        {title && (
          <Typography
            variant="h6"
            component="h2"
            sx={{
              display: 'flex',
              alignItems: 'center',

              flexWrap: 'wrap',
              lineHeight: '1.5rem',
            }}
          >
            {title}
            {title === 'Experiments' && selectedRow && data ? (
              <>
                <ChevronRightIcon fontSize="small" />
                <Typography variant="body2">
                  {JSON.parse(localStorage.getItem('selectedExperiment'))?.name}
                </Typography>
              </>
            ) : (
              ''
            )}
          </Typography>
        )}
        <Activity mode={showActions ? 'visible' : 'hidden'}>
          <Box>
            <IconButton
              aria-label="more"
              id="long-button"
              aria-controls={open ? 'long-menu' : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-haspopup="true"
              onClick={handleMenu}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => {
                  handleExport(data, 'csv')
                  handleClose()
                }}
              >
                <ListItemIcon>
                  <FileDownload fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export CSV</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleExport(data, 'xlsx')
                  handleClose()
                }}
              >
                <ListItemIcon>
                  <FileDownload fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export XLSX</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Activity>
      </Grid>

      <DataGrid
        getRowClassName={(params) =>
          selectedRow && params.row.id === selectedRow ? 'selected-row' : ''
        }
        sortingMode="server"
        filterMode="server"
        paginationMode="server"
        keepNonExistentRowsSelected
        sx={{
          borderTopLeftRadius: title ? 0 : '',
          borderTopRightRadius: title ? 0 : '',
          '.MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          '.MuiDataGrid-footerContainer': {
            fontSize: '0.75rem !important',
          },
          '& .selected-row': {
            backgroundColor: `${theme.palette.action.selected}`,
            boxShadow: `${theme.palette.primary.main} 4px 0px 0px 0px inset`,
          },
        }}
        rows={data || []}
        getRowId={(row) => row.uuid || row.id}
        columns={processedColumns}
        disableRowSelectionOnClick
        checkboxSelection={checkboxSelection}
        pageSizeOptions={pageSizeOptions}
        pageSize={pageSize}
        onRowClick={onRowClick ? onRowClick : handleRowClick}
        loading={data ? false : true}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        rowCount={rowCount}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        // onRowSelectionModelChange={onRowSelectionModelChange}
        // rowSelectionModel={rowSelectionModel}
        slots={{
          ...slots,
        }}
        slotProps={{
          basePagination: {
            SelectProps: {
              native: true,
              inputProps: {
                'aria-label': 'Rows per page',
              },
              sx: {
                '& select:focus': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                },
              },
            },
          },
        }}
        disableColumnSelector={isMobile ? false : disableColumnSelector}
        disableDensitySelector
        {...params}
      />
    </Grid>
  )
}

export default HoloTable
